import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface ServerStatusProps {
  onStatusChange?: (isHealthy: boolean) => void;
}

const ServerStatus: React.FC<ServerStatusProps> = ({ onStatusChange }) => {
  const [status, setStatus] = useState<'checking' | 'healthy' | 'error'>('checking');
  const [message, setMessage] = useState<string>('Checking server status...');
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  const checkServerStatus = async () => {
    setStatus('checking');
    setMessage('Checking server status...');
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch('/api/health', {
        method: 'GET',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        setStatus('healthy');
        setMessage(`Server is healthy - ${data.message}`);
        onStatusChange?.(true);
      } else {
        setStatus('error');
        setMessage(`Server returned status ${response.status}`);
        onStatusChange?.(false);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Cannot connect to optimization server');
      onStatusChange?.(false);
      console.error('Server status check failed:', error);
    } finally {
      setLastCheck(new Date());
    }
  };

  useEffect(() => {
    checkServerStatus();
    
    // Check status every 30 seconds
    const interval = setInterval(checkServerStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = () => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'checking':
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'healthy':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'checking':
      default:
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    }
  };

  return (
    <div className={`p-3 border rounded-lg ${getStatusColor()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <div>
            <p className="font-medium text-sm">
              Optimization Server
            </p>
            <p className="text-xs opacity-75">
              {message}
            </p>
            {lastCheck && (
              <p className="text-xs opacity-50">
                Last checked: {lastCheck.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={checkServerStatus}
          className="p-1 rounded hover:bg-opacity-20 hover:bg-black transition-colors"
          title="Refresh server status"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ServerStatus;
